import { logger } from '../../../utils/core/logger';
import type { FormDetectionContext, FormDetectionStrategy, FormElement } from '../types';

// Strategy 1: Superform Multi-Step Detection (Webflow Superform library)
export class SuperformStrategy implements FormDetectionStrategy {
    name = 'superform_multistep';
    private trackedSubmissions = new Set<string>(); // Track submitted forms to prevent duplicates
    private observers: MutationObserver[] = [];

    constructor(
        private stepAttributeName: string = 'sf-step',
        private targetStepValue: string = 'step9'
    ) {}

    canHandle(form: FormElement): boolean {
        // Detect Webflow Superform multi-step forms
        // The sf attribute can be on the form itself, a parent container, or the form can contain sf-step elements
        const hasSfAttribute = form.hasAttribute('sf');
        const hasStepChild = form.querySelector('[sf-step]') !== null;

        // Check if form is inside a parent with sf attribute
        let hasParentWithSf = false;
        let parentElement = form.parentElement;
        while (parentElement) {
            if (parentElement.hasAttribute('sf')) {
                hasParentWithSf = true;
                break;
            }
            parentElement = parentElement.parentElement;
        }

        const hasSuperformAttributes = hasSfAttribute || hasStepChild || hasParentWithSf;

        logger.debug('🎯 SuperformStrategy.canHandle check', {
            formId: form.id,
            formTagName: form.tagName,
            hasSfAttribute,
            hasStepChild,
            hasParentWithSf,
            result: hasSuperformAttributes,
        });

        return hasSuperformAttributes;
    }

    apply(form: FormElement, context: FormDetectionContext): void {
        // Method 1: Listen for Superform custom completion event
        this.setupSuperformCustomEvents(form, context);

        // Method 2: Track step progression (when user moves past email step)
        this.setupStepProgressionTracking(form, context);
    }

    // Listen for Superform's custom completion events
    private setupSuperformCustomEvents(form: FormElement, context: FormDetectionContext): void {
        const superformContainer = form.closest('[sf]');
        if (!superformContainer) {
            return;
        }

        // Listen for sf:complete event
        superformContainer.addEventListener('sf:complete', () => {
            // Check deduplication
            const trackingKey = `${form.id}-complete`;
            if (this.trackedSubmissions.has(trackingKey)) {
                return;
            }
            this.trackedSubmissions.add(trackingKey);

            logger.debug('✅ Superform completion event - tracking submission');

            const fieldCount = form.querySelectorAll('input, select, textarea').length;
            const redirectUrl = form.getAttribute('redirect') || form.getAttribute('data-redirect');

            context.onFormSubmit({
                formId: form.id || 'superform',
                formClass: form.className || '',
                pageUrl: context.currentUrl,
                formAction: form.action || redirectUrl || '',
                formMethod: form.method || 'GET',
                fieldCount,
                detectionMethod: this.name,
                status: 'success',
                indicators: ['superform_custom_event_sf_complete'],
            });

            // Clean up after 10 seconds
            setTimeout(() => {
                this.trackedSubmissions.delete(trackingKey);
            }, 10000);
        });
    }

    // Track form progression by watching for target step to become visible
    private setupStepProgressionTracking(form: FormElement, context: FormDetectionContext): void {
        // Get the parent container with sf attribute
        const superformContainer = form.closest('[sf]') || form.parentElement?.closest('[sf]');

        if (!superformContainer) {
            return;
        }

        // Find the target step using configured attribute and value
        const targetStep = form.querySelector(
            `[${this.stepAttributeName}="${this.targetStepValue}"]`
        ) as HTMLElement;

        if (!targetStep) {
            logger.debug('⚠️ Target step not found', {
                stepAttributeName: this.stepAttributeName,
                targetStepValue: this.targetStepValue,
            });
            return;
        }

        // Use MutationObserver to watch for the target step to become visible
        const observer = new MutationObserver(() => {
            // Check if the target step is now VISIBLE
            const targetStepVisible = window.getComputedStyle(targetStep).display !== 'none';

            // Check deduplication
            const trackingKey = `${form.id}-${this.targetStepValue}`;

            if (targetStepVisible && !this.trackedSubmissions.has(trackingKey)) {
                // Target step is now VISIBLE = user reached the target step
                this.trackedSubmissions.add(trackingKey);

                logger.debug('✅ Superform target step reached - tracking submission', {
                    targetStepValue: this.targetStepValue,
                });

                const fieldCount = form.querySelectorAll('input, select, textarea').length;
                const redirectUrl =
                    form.getAttribute('redirect') || form.getAttribute('data-redirect');

                context.onFormSubmit({
                    formId: form.id || 'superform',
                    formClass: form.className || '',
                    pageUrl: context.currentUrl,
                    formAction: form.action || redirectUrl || '',
                    formMethod: form.method || 'GET',
                    fieldCount,
                    detectionMethod: this.name,
                    status: 'success',
                    indicators: [
                        `superform_target_step_reached:${this.targetStepValue}`,
                        'multi_step_progression',
                    ],
                });

                // Clean up after 10 seconds
                setTimeout(() => {
                    this.trackedSubmissions.delete(trackingKey);
                }, 10000);

                // Stop observing after tracking
                observer.disconnect();
            }
        });

        // Track observer for cleanup
        this.observers.push(observer);

        // Observe the superform container for any changes
        observer.observe(superformContainer, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class'],
        });
    }

    cleanup(): void {
        this.observers.forEach((o) => {
            o.disconnect();
        });
        this.observers = [];
        this.trackedSubmissions.clear();
    }
}
